import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import type { HostPlayPostMessageConnectionViewApi } from '@genially/contracts';
import { InteractiveQuestionType } from '@genially/document-model';
import { HostPlayP2pClientEventType } from '@genially/p2p-client';
import type { AliasMode, PlayerData, ReactionType, ResultsMode } from '@genially/p2p-lib';
import type { PenpalAsyncMethodReturns } from '@genially/ui';
import { isInteractiveQuestionItemData } from '@genially/ui/build/components/SidebarSummary/utils/isInteractiveQuestion';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';
import { calculate } from '@genially/utils/build/module';

import { PlaySpinner } from '../../../../../../shared/application/components/PlaySpinner/PlaySpinner';
import { useGetInteractiveQuestion } from '../../../../../../shared/application/hooks/useGetInteractiveQuestion';
import { useReactions } from '../../../../../../shared/application/hooks/useReactions';
import type { ActiveReaction } from '../../../../../../shared/application/types/reactions';
import { MAX_CONCURRENT_REACTIONS } from '../../../../../../shared/application/utils/reactions';
import type { PlayPostMessageInteractiveQuestion } from '../../../../../../shared/domain/types/PlayPostMessageInteractiveQuestion';
import { useHostClientContext } from '../HostClientProvider/hooks/useHostClientContext';

type ViewConnectionState =
  | {
      isConnected: false;
      connection: undefined;
      setConnection: (
        connection:
          | PenpalAsyncMethodReturns<HostPlayPostMessageConnectionViewApi>
          | undefined,
      ) => void;
    }
  | {
      isConnected: true;
      connection: PenpalAsyncMethodReturns<HostPlayPostMessageConnectionViewApi>;
      setConnection: (
        connection:
          | PenpalAsyncMethodReturns<HostPlayPostMessageConnectionViewApi>
          | undefined,
      ) => void;
    };
type HostGameContextSelectedQuestion = {
  interactiveQuestion: PlayPostMessageInteractiveQuestion;
  isLastScored: boolean;
};

export type HostGameContextType = {
  viewConnection: ViewConnectionState;

  isOpenMode: boolean;

  sidebarSummary: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;

    selectedQuestion: HostGameContextSelectedQuestion | undefined;
    setSelectedQuestion: (question: HostGameContextSelectedQuestion) => void;
    removeSelectedQuestion: () => void;
  };

  /**
   * The aggregated results panel, shown instead of the ranking in `analytics` mode. Two
   * sibling views inside the same panel: with a question selected it reads that question,
   * without one it reads the session.
   */
  analyticsSidebar: {
    isOpen: boolean;
    selectedQuestionId: string | undefined;
    open: () => void;
    openQuestion: (interactiveQuestionId: string) => void;
    close: () => void;
  };

  settingsSidebar: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
  };

  participantsSidebar: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
  };

  inviteSidebar: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
  };

  instructions: {
    isVisible: boolean;
    toggle: () => void;
    isMouseNearTopOfIframe: boolean;
    setIsMouseNearTopOfIframe: (isNear: boolean) => void;
  };

  interactiveQuestions: PlayPostMessageInteractiveQuestion[];
  interactiveQuestionsResolved: number;

  reactions: ActiveReaction[];

  handQueueSidebar: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
  };

  handQueue: {
    queue: PlayerData[];
    dismiss: (playerId: string) => void;
    dismissAll: () => void;
  };

  reactionsConfig: { isEnabled: boolean; toggle: () => void };
  raiseHandConfig: { isEnabled: boolean; toggle: () => void };
  hostCursorConfig: { isEnabled: boolean; toggle: () => void };
  resultsModeConfig: { mode: ResultsMode; setMode: (mode: ResultsMode) => void };
  aliasModeConfig: { mode: AliasMode; setMode: (mode: AliasMode) => void };

  shouldAutoOpenSessionConfigModal: boolean;

  initialSessionConfigModal: {
    isOpen: boolean;
    close: () => void;
  };
};

const HostGameContext = createContext({});

export interface HostGameProps {
  children: ReactNode;
}

const HostGameProvider = observer(({ children }: HostGameProps) => {
  const { client, isNewSession } = useHostClientContext();

  const isOpenMode = client.hasFeatureFlag(FEATURE_FLAGS.FEAT_LT4_PLAY_OPEN_MODE);

  // New session + flag on: show the config modal first (HostGameContent triggers it), and
  // only open the invite sidebar once the host dismisses it — see isInviteSidebarOpen below.
  const shouldAutoOpenSessionConfigModal =
    isNewSession &&
    client.hasFeatureFlag(FEATURE_FLAGS.FEAT_LT4_PLAY_INITIAL_SESSION_CONFIG_MODAL);

  const { isLoading: isLoadingInteractiveQuestions, interactiveQuestions } =
    useGetInteractiveQuestion(client.geniallyId);

  const [connection, setConnection] = useState<
    PenpalAsyncMethodReturns<HostPlayPostMessageConnectionViewApi> | undefined
  >();

  const [isSidebarSummaryOpen, setIsSidebarSummaryOpen] = useState(false);

  const [isAnalyticsSidebarOpen, setIsAnalyticsSidebarOpen] = useState(false);

  const [analyticsQuestionId, setAnalyticsQuestionId] = useState<string | undefined>();

  const [isSettingsSidebarOpen, setIsSettingsSidebarOpen] = useState(false);

  const [isParticipantsSidebarOpen, setIsParticipantsSidebarOpen] = useState(false);

  // Open mode never renders the lobby, so this is the host's only invite screen — it
  // starts open rather than waiting for the host to find and click a way to open it.
  // Unless the initial config modal is about to show first, in which case it opens only
  // once that modal is dismissed (see HostGameContent).
  const [isInviteSidebarOpen, setIsInviteSidebarOpen] = useState(
    isOpenMode && !shouldAutoOpenSessionConfigModal,
  );

  // The game control header stays out of the way during the initial onboarding: it is
  // hidden while the config modal is up and, in open mode, until the invite sidebar the
  // modal hands over to is closed (see GameControlHeader).
  const [isInitialSessionConfigModalOpen, setIsInitialSessionConfigModalOpen] = useState(
    shouldAutoOpenSessionConfigModal,
  );

  const [isInstructionsVisible, setIsInstructionsVisible] = useState(true);
  const [isMouseNearTopOfIframe, setIsMouseNearTopOfIframe] = useState(false);

  const handleSetIsMouseNearTopOfIframe = useCallback((isNear: boolean) => {
    setIsMouseNearTopOfIframe(isNear);
  }, []);

  const [selectedQuestion, setSelectedQuestion] = useState<
    HostGameContextSelectedQuestion | undefined
  >(undefined);

  const reactions = useReactions(client, MAX_CONCURRENT_REACTIONS);

  const [isHandQueueSidebarOpen, setIsHandQueueSidebarOpen] = useState(false);

  const viewConnection = calculate(() => {
    if (connection) {
      return {
        isConnected: true,
        setConnection,
        connection,
      };
    }

    return {
      isConnected: false,
      setConnection,
    };
  });

  const openSidebarSummary = useCallback(() => {
    setIsSidebarSummaryOpen(true);
  }, []);

  const closeSidebarSummary = useCallback(() => {
    setIsSidebarSummaryOpen(false);
  }, []);

  const toggleSidebarSummary = useCallback(() => {
    setIsSidebarSummaryOpen(prev => !prev);
  }, []);

  const closeAnalyticsSidebar = useCallback(() => {
    setIsAnalyticsSidebarOpen(false);
    setAnalyticsQuestionId(undefined);
  }, []);

  const openAnalyticsSidebar = useCallback(() => {
    // Both panels live on the right edge, so they are mutually exclusive.
    setIsSidebarSummaryOpen(false);
    setAnalyticsQuestionId(undefined);
    setIsAnalyticsSidebarOpen(true);
  }, []);

  // Resolving a question, or asking for its answers from inside the genially, replaces
  // whatever the panel was showing rather than opening a second one beside it.
  const openAnalyticsQuestion = useCallback((interactiveQuestionId: string) => {
    setIsSidebarSummaryOpen(false);
    setAnalyticsQuestionId(interactiveQuestionId);
    setIsAnalyticsSidebarOpen(true);
  }, []);

  const openSettingsSidebar = useCallback(() => {
    setIsSettingsSidebarOpen(true);
  }, []);

  const closeSettingsSidebar = useCallback(() => {
    setIsSettingsSidebarOpen(false);
  }, []);

  const toggleSettingsSidebar = useCallback(() => {
    setIsSettingsSidebarOpen(prev => !prev);
  }, []);

  const openParticipantsSidebar = useCallback(() => {
    setIsParticipantsSidebarOpen(true);
  }, []);

  const closeParticipantsSidebar = useCallback(() => {
    setIsParticipantsSidebarOpen(false);
  }, []);

  const toggleParticipantsSidebar = useCallback(() => {
    setIsParticipantsSidebarOpen(prev => !prev);
  }, []);

  const openInviteSidebar = useCallback(() => {
    setIsInviteSidebarOpen(true);
  }, []);

  const closeInviteSidebar = useCallback(() => {
    setIsInviteSidebarOpen(false);
  }, []);

  const toggleInviteSidebar = useCallback(() => {
    setIsInviteSidebarOpen(prev => !prev);
  }, []);

  const openHandQueueSidebar = useCallback(() => {
    setIsHandQueueSidebarOpen(true);
  }, []);

  const closeHandQueueSidebar = useCallback(() => {
    setIsHandQueueSidebarOpen(false);
  }, []);

  const toggleHandQueueSidebar = useCallback(() => {
    setIsHandQueueSidebarOpen(prev => !prev);
  }, []);

  const dismissAllPlayerHands = useCallback(() => {
    client.game.clearHandQueue();
  }, [client]);

  const dismissPlayerHand = useCallback(
    (playerId: string) => {
      client.game.removeFromHandQueue(playerId);
    },
    [client],
  );

  const closeInitialSessionConfigModal = useCallback(() => {
    setIsInitialSessionConfigModalOpen(false);
  }, []);

  const toggleInstructions = useCallback(() => {
    setIsInstructionsVisible(prev => !prev);
  }, []);

  const removeSelectedQuestion = useCallback(() => {
    setSelectedQuestion(undefined);
  }, []);

  const sidebarSummary = {
    isOpen: isSidebarSummaryOpen,
    selectedQuestion,
    setSelectedQuestion,
    removeSelectedQuestion,
    open: openSidebarSummary,
    close: closeSidebarSummary,
    toggle: toggleSidebarSummary,
  };

  // What the genially's own "N | See answers" button reads to label itself. The analytics
  // view of a question reports through the same channel as the individual summary: it is
  // the same promise to the host — the answers to this question are on screen — so the
  // button has to say "Hide answers" for it too.
  useEffect(() => {
    if (!connection) return;

    if (isAnalyticsSidebarOpen && analyticsQuestionId) {
      connection.setSidebarSummaryState({
        open: true,
        interactiveQuestionId: analyticsQuestionId,
      });

      return;
    }

    if (isSidebarSummaryOpen && selectedQuestion) {
      connection.setSidebarSummaryState({
        open: true,
        interactiveQuestionId: selectedQuestion.interactiveQuestion.Id,
      });

      return;
    }

    connection.setSidebarSummaryState({ open: false });
  }, [
    connection,
    isAnalyticsSidebarOpen,
    analyticsQuestionId,
    isSidebarSummaryOpen,
    selectedQuestion,
  ]);

  // The genially renders the host's own buttons, so it needs to know whether the session
  // reads results through the analytics panel: a resolved question is a dead end in every
  // other mode, but in this one its button is the way in and out of its analytics view.
  useEffect(() => {
    if (!connection) return;

    connection.setIsAnalyticsResultsMode(client.game.resultsMode === 'analytics');
  }, [connection, client.game.resultsMode]);

  useEffect(() => {
    if (!isSidebarSummaryOpen) {
      setSelectedQuestion(undefined);
    }
  }, [isSidebarSummaryOpen]);

  // The other half of the exclusion above: a question summary opening — on resolve, or
  // from the ranking link — takes the right edge back from the analytics panel. Keeping it
  // here rather than at each opener means any future one is covered too.
  useEffect(() => {
    if (isSidebarSummaryOpen) {
      setIsAnalyticsSidebarOpen(false);
    }
  }, [isSidebarSummaryOpen]);

  // A question's analytics view belongs to the slide the question is on, so it goes when the
  // host moves on; the session view is not tied to any slide and stays. Read from the game
  // rather than the genially's slide-change call, whose handler penpal captures only once.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a slide change should close it
  useEffect(() => {
    if (analyticsQuestionId) {
      closeAnalyticsSidebar();
    }
  }, [client.game.currentSlide]);

  const handleInteractiveQuestionResolved = useCallback(
    async ({
      interactiveQuestionId,
      isLastScored,
    }: {
      interactiveQuestionId: string;
      isLastScored?: boolean;
    }) => {
      const interactiveQuestion = interactiveQuestions.find(
        i => i.Id === interactiveQuestionId,
      );

      if (
        !interactiveQuestion ||
        (isInteractiveQuestionItemData(interactiveQuestion) &&
          interactiveQuestion.Type === InteractiveQuestionType.BranchingScenario)
      ) {
        return;
      }

      // The analytics mode has its own reading of every question type, so it replaces the
      // individual summary rather than living beside it: one visual language per mode.
      if (client.game.resultsMode === 'analytics') {
        openAnalyticsQuestion(interactiveQuestionId);

        return;
      }

      // "No results" means no results: unlike before, not even the summary of a feedback
      // question opens for the host. The participants keep getting their own feedback.
      if (client.game.resultsMode === 'none') {
        return;
      }

      setSelectedQuestion({ interactiveQuestion, isLastScored: isLastScored ?? false });

      setIsSidebarSummaryOpen(true);
    },
    [client, interactiveQuestions, openAnalyticsQuestion],
  );

  useEffect(() => {
    client.on(
      HostPlayP2pClientEventType.InteractiveQuestionResolved,
      handleInteractiveQuestionResolved,
    );

    return () => {
      client.off(
        HostPlayP2pClientEventType.InteractiveQuestionResolved,
        handleInteractiveQuestionResolved,
      );
    };
  }, [client, handleInteractiveQuestionResolved]);

  useEffect(() => {
    const handleBroadcast = ({
      player,
      reactionType,
    }: {
      player: PlayerData;
      reactionType: ReactionType;
    }) => {
      if (!client.game.isReactionsEnabled) return;
      client.broadcastReaction(player, reactionType);
    };

    client.on(HostPlayP2pClientEventType.PlayerReactionReceived, handleBroadcast);

    return () => {
      client.off(HostPlayP2pClientEventType.PlayerReactionReceived, handleBroadcast);
    };
  }, [client]);

  // client.game is a live MobX observable — isReactionsEnabled / isRaiseHandEnabled
  // are always the current values inside these callbacks. Omitting them from
  // deps is intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleReactions = useCallback(() => {
    client.setIsReactionsEnabled(!client.game.isReactionsEnabled);
  }, [client]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleRaiseHand = useCallback(() => {
    const raiseHandEnabled = !client.game.isRaiseHandEnabled;
    client.setIsRaiseHandEnabled(raiseHandEnabled);
    if (!raiseHandEnabled) {
      client.game.clearHandQueue();
    }
  }, [client]);

  const [isHostCursorEnabled, setIsHostCursorEnabled] = useState(false);
  const toggleHostCursor = useCallback(() => setIsHostCursorEnabled(prev => !prev), []);

  const setResultsMode = useCallback(
    (mode: ResultsMode) => {
      client.setResultsMode(mode);

      if (mode !== 'analytics') {
        // The link that opens this panel disappears from the settings sidebar with the
        // mode, so an already-open panel has to go with it.
        closeAnalyticsSidebar();
      }

      // Whatever the summary was showing belongs to the mode that was active when it
      // opened, so it goes with the mode: leaving it up would have the host reading a
      // question through a lens the session no longer uses.
      closeSidebarSummary();
    },
    [client, closeAnalyticsSidebar, closeSidebarSummary],
  );

  if (isLoadingInteractiveQuestions) {
    return <PlaySpinner />;
  }

  const analyticsSidebar = {
    isOpen: isAnalyticsSidebarOpen,
    selectedQuestionId: analyticsQuestionId,
    open: openAnalyticsSidebar,
    openQuestion: openAnalyticsQuestion,
    close: closeAnalyticsSidebar,
  };

  const settingsSidebar = {
    isOpen: isSettingsSidebarOpen,
    open: openSettingsSidebar,
    close: closeSettingsSidebar,
    toggle: toggleSettingsSidebar,
  };

  const participantsSidebar = {
    isOpen: isParticipantsSidebarOpen,
    open: openParticipantsSidebar,
    close: closeParticipantsSidebar,
    toggle: toggleParticipantsSidebar,
  };

  const inviteSidebar = {
    isOpen: isInviteSidebarOpen,
    open: openInviteSidebar,
    close: closeInviteSidebar,
    toggle: toggleInviteSidebar,
  };

  const initialSessionConfigModal = {
    isOpen: isInitialSessionConfigModalOpen,
    close: closeInitialSessionConfigModal,
  };

  const instructions = {
    isVisible: isInstructionsVisible,
    toggle: toggleInstructions,
    isMouseNearTopOfIframe,
    setIsMouseNearTopOfIframe: handleSetIsMouseNearTopOfIframe,
  };

  const handQueueSidebar = {
    isOpen: isHandQueueSidebarOpen,
    open: openHandQueueSidebar,
    close: closeHandQueueSidebar,
    toggle: toggleHandQueueSidebar,
  };

  const reactionsConfig = {
    isEnabled: client.game.isReactionsEnabled,
    toggle: toggleReactions,
  };

  const raiseHandConfig = {
    isEnabled: client.game.isRaiseHandEnabled,
    toggle: toggleRaiseHand,
  };

  const hostCursorConfig = {
    isEnabled: isHostCursorEnabled,
    toggle: toggleHostCursor,
  };

  const resultsModeConfig = {
    mode: client.game.resultsMode,
    setMode: setResultsMode,
  };

  const aliasModeConfig = {
    mode: client.game.aliasMode,
    setMode: (mode: AliasMode) => client.setAliasMode(mode),
  };

  const context: HostGameContextType = {
    viewConnection: viewConnection as ViewConnectionState,
    isOpenMode,
    sidebarSummary,
    analyticsSidebar,
    settingsSidebar,
    participantsSidebar,
    inviteSidebar,
    instructions,
    interactiveQuestions,
    interactiveQuestionsResolved: [
      ...client.game.interactiveQuestionAnswers.values(),
    ].filter(value => value.isResolved).length,
    reactions,
    handQueueSidebar,
    handQueue: {
      queue: client.game.handQueue,
      dismiss: dismissPlayerHand,
      dismissAll: dismissAllPlayerHands,
    },
    reactionsConfig,
    raiseHandConfig,
    hostCursorConfig,
    resultsModeConfig,
    aliasModeConfig,
    shouldAutoOpenSessionConfigModal,
    initialSessionConfigModal,
  };

  return <HostGameContext.Provider value={context}>{children}</HostGameContext.Provider>;
});

const useHostGameContext = () => {
  const context = useContext(HostGameContext) as HostGameContextType;

  if (Object.keys(context).length === 0) {
    throw new Error('useHostGameContext must be used within a HostGameProvider');
  }

  return context;
};

export { HostGameProvider, useHostGameContext };
